import {course_getParents_Api} from 'apis'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {useLocation} from 'react-use'
import {branch, compose} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey} from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault} from 'views/Shared'
import {StudentFilter} from '../components/filters/StudentFilter'
import {useRouteSearchParam} from '../../../../envs/_current/history'
import EmptyHolder from 'components/EmptyHolder'
import classNames from 'classnames'
import {LayoutContext} from '../../../../components/layouts/Default/LayoutContext'
import PagePaginationWrapper from "../../../../modules/asyncCache/components/PagePagination/PagePaginationWrapper"
import {renderLoading} from "../../../AppLocals/functions/renderers/renderLoading"
import {renderParentCardItem, renderParentTableRow} from '../functions/renderParent'
import {BsGrid3X3GapFill, BsTable} from "react-icons/bs"

const Parents = ({
    owner_id,
    owner_type,
    filter_actions = {
        sortBy: 'sort-by',
        keyword: 'keyword',
        course_id: 'course',
        course_class_id: 'course-class',
        sub_class_name: 'sub-class-name',
        include_archived: 'include-archived',
    },
}) => {
    const t = useTranslate()
    const location = useLocation()
    const {isLg} = useContext(LayoutContext)
    // Bảng mặc định desktop, Thẻ mặc định mobile - khớp cách đã làm ở Students.js/Teachers.js.
    const [viewMode, setViewMode] = useState(() => isLg ? 'table' : 'card')

    const sortBy = useRouteSearchParam(filter_actions.sortBy)
    const course_id = useRouteSearchParam(filter_actions.course_id)
    const course_class_id = useRouteSearchParam(filter_actions.course_class_id)
    const sub_class_name = useRouteSearchParam(filter_actions.sub_class_name)
    const include_archived = useRouteSearchParam(filter_actions.include_archived)

    const refresh_deps = useMemo(
        () => [owner_id, owner_type, course_id, course_class_id, `refreshToken=${_.get(location.state, 'refreshToken')}`],
        [owner_id, owner_type, location.state]
    )

    // Bảng: cột #|PHỤ HUYNH (CHA/MẸ)|HỌC VIÊN CON ĐANG HỌC|LIÊN HỆ PH|THAO TÁC (theo mockup) - khớp
    // cột với renderParentTableRow (renderParent.js).
    const TABLE_COLUMNS = [
        {label: '#', className: 'text-center', style: {width: 40}},
        {label: t('parents'), className: 'text-left'},
        {label: t('children studying'), className: 'text-left'},
        {label: t('parent contact'), className: 'text-left'},
        {label: t('actions'), className: 'text-right'},
    ]

    const renderList = useCallback(
        ({keyword, sortBy: currentSort, filterBy, refreshKey}) => (
            <PagePaginationWrapper
                apiInfo={course_getParents_Api}
                query={bindQueryParams([{id: owner_id}, {type: owner_type}])}
                values={{...filter_actions, keyword, sort_by: currentSort, ...(filterBy ?? {})}}
                refreshKey={refreshKey}
                renderItem={
                    viewMode === 'table'
                        ? renderParentTableRow({translate: t})
                        : renderParentCardItem({translate: t})
                }
                renderLoading={() => renderLoading()}
                renderEmpty={() => <EmptyHolder/>}
                Wrapper={({children}) => viewMode === 'table' ? (
                    <div className="mt-2 w-full overflow-x-auto rounded-xl border border-color-50">
                        <table className="w-full" style={{borderCollapse: 'collapse'}}>
                            <thead>
                                <tr className="background-200">
                                    {TABLE_COLUMNS.map((col, i) => (
                                        <th key={i} style={col.style} className={classNames("px-3 py-2 text-2xs font-bold text-color-400 uppercase tracking-wider whitespace-no-wrap", col.className)}>
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>{children}</tbody>
                        </table>
                    </div>
                ) : (
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                        {children}
                    </div>
                )}
            />
        ),
        [owner_id, owner_type, t, viewMode, filter_actions]
    )

    const viewModeToggle = (
        <div className="flex items-center flex-shrink-0 rounded-lg border border-color-200 overflow-hidden">
            <button
                type="button"
                title={t('card')}
                onClick={() => setViewMode('card')}
                style={{width: 40, height: 40}}
                className={classNames(
                    "flex flex-center transition-colors",
                    viewMode === 'card' ? 'bg-secondary-500 text-white' : 'background text-color-300 hover:text-secondary-600'
                )}>
                <BsGrid3X3GapFill size={14}/>
            </button>
            <button
                type="button"
                title={t('table')}
                onClick={() => setViewMode('table')}
                style={{width: 40, height: 40}}
                className={classNames(
                    "flex flex-center transition-colors border-l border-color-200",
                    viewMode === 'table' ? 'bg-secondary-500 text-white' : 'background text-color-300 hover:text-secondary-600'
                )}>
                <BsTable size={14}/>
            </button>
        </div>
    )

    const [pager_widget] = usePaginationHelper({
        filter_params: {owner_id, owner_type},
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onFilterChange({course_id, course_class_id, sub_class_name, include_archived})
            onSortChange(sortBy)
        },
        renderList,
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        // Toggle Thẻ/Bảng đặt cạnh ô search (renderHeader) - không đụng StudentFilter/search bar,
        // khớp đúng cách đã làm ở Students.js/Teachers.js.
        renderHeader: ([search_bar]) => (
            <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">{search_bar}</div>
                {viewModeToggle}
            </div>
        ),
        dependencies: [owner_id, owner_type, course_id, course_class_id, sub_class_name, include_archived],
        query_params: filter_actions,
        withKey: createPagerKey(refresh_deps),
        AdvancedFilter: StudentFilter,
        isToggleDefault: false,
    })

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col flex-1">
                <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                    {t('parent management')}
                </h2>
                <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                <p className="mt-1 text-sm text-color-300 tracking-wider">
                    {t('manage the list of parents contact information for all students in the workspace')}
                </p>
            </div>
            {pager_widget}
            <div className="h-16"/>
        </div>
    )
}

export default compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(Parents)
